import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { cleanText, wrapText, getNodeCategory } from './LayoutEngine/engine.js';
import { NODE_WIDTH } from '../../../Config/ExistingVisaTreeViewConsts.js';

const ADMIN_GREEN = '#8DDE43';
const ADMIN_DARK_GREEN = '#078346';
const ADMIN_GRAY = '#757575';
const ADMIN_DARK_GRAY = '#4e4e4e';

const COLORS = {
  start: {
    fill: '#ffffff',
    border: ADMIN_DARK_GRAY,
    headerBg: ADMIN_DARK_GRAY,
    headerText: '#ffffff',
    text: ADMIN_DARK_GRAY,
  },
  decision: {
    fill: '#ffffff',
    border: ADMIN_GREEN,
    headerBg: ADMIN_GREEN,
    headerText: '#000000',
    text: ADMIN_DARK_GRAY,
  },
  outcome: {
    fill: '#ffffff',
    border: ADMIN_DARK_GREEN,
    headerBg: ADMIN_DARK_GREEN,
    headerText: '#ffffff',
    text: ADMIN_DARK_GRAY,
  },
};

const getNodeIconPath = (category) => {
  switch (category) {
    case 'start':
      return 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5';
    case 'outcome':
      return 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3';
    default:
      return 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  }
};

const MAX_TITLE_LINES = 2;
const MAX_TEXT_LINES = 3;

const CustomNode = memo(({ data }) => {
  const { node, hasChildren, onNodeClick } = data;
  const category = getNodeCategory(node, hasChildren);
  const colors = COLORS[category];

  const title = cleanText(node.title);
  const text = cleanText(node.text);

  const allTitleLines = wrapText(title, 34);
  const allTextLines = wrapText(text, 38);

  const displayTitleLines = allTitleLines.slice(0, MAX_TITLE_LINES);
  const displayTextLines = allTextLines.slice(0, MAX_TEXT_LINES);

  if (allTitleLines.length > MAX_TITLE_LINES && displayTitleLines.length > 0) {
    displayTitleLines[displayTitleLines.length - 1] =
      displayTitleLines[displayTitleLines.length - 1].slice(0, -3) + '...';
  }
  if (allTextLines.length > MAX_TEXT_LINES && displayTextLines.length > 0) {
    displayTextLines[displayTextLines.length - 1] =
      displayTextLines[displayTextLines.length - 1].slice(0, -3) + '...';
  }

  return (
    <div
      className="visa-node"
      style={{
        width: NODE_WIDTH,
        background: colors.fill,
        border: `2px solid ${colors.border}`,
        borderRadius: 8,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        fontFamily: "'Open Sans', sans-serif",
        overflow: 'hidden',
      }}
    >
      <Handle type="target" position={Position.Top} className="visa-node-handle" />

      {/* Header */}
      <div
        className="visa-node-header"
        style={{
          background: colors.headerBg,
          padding: '0 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          flexShrink: 0,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colors.headerText}
          strokeWidth="2"
          style={{ flexShrink: 0 }}
        >
          <path d={getNodeIconPath(category)} />
        </svg>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: colors.headerText,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          {category}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 10,
            fontWeight: 600,
            color: colors.headerText,
            opacity: 0.85,
          }}
        >
          ID: {node.id}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: '12px 14px 8px' }}>
        {title && (
          <div style={{ marginBottom: text ? 6 : 0 }}>
            {displayTitleLines.map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                  lineHeight: 1.4,
                }}
              >
                {line}
              </div>
            ))}
          </div>
        )}

        {text && (
          <div>
            {displayTextLines.map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: 12,
                  color: colors.text,
                  opacity: 0.85,
                  lineHeight: 1.4,
                }}
              >
                {line}
              </div>
            ))}
          </div>
        )}

        {!title && !text && (
          <div
            style={{
              fontSize: 11,
              color: ADMIN_GRAY,
              fontStyle: 'italic',
            }}
          >
            (No content)
          </div>
        )}
      </div>

      {/* Preview Button */}
      <div style={{ padding: '0 14px 12px' }}>
        <button
          className="visa-node-preview-btn"
          onClick={(e) => {
            e.stopPropagation();
            onNodeClick && onNodeClick(node);
          }}
          style={{
            width: '100%',
            padding: '5px 8px',
            background: '#f0f7ec',
            border: `1px solid ${ADMIN_DARK_GREEN}`,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            color: ADMIN_DARK_GREEN,
            cursor: 'pointer',
            fontFamily: "'Open Sans', sans-serif",
          }}
        >
          {category === 'outcome' ? 'Result Preview' : 'Question Preview'}
        </button>
      </div>

      <Handle type="source" position={Position.Bottom} className="visa-node-handle" />
    </div>
  );
});

export default CustomNode;